import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import config from '../config'; // adjust path if needed

const emptyStream = () => ({
  status: 'idle', // idle | loading | done | error
  message: '',
  error: null,
  rows: [],
  months: [], // union of month columns seen so far
  sectionsLoaded: [], // section ids fully received, in arrival order
  stale: false, // newer data exists and is being loaded in the background
});

const initialState = {
  // Streaming consolidation (rows arrive section by section, traces last)
  actualStream: emptyStream(),
  predictedStream: emptyStream(),

  // Variance States (NEW)
  varianceData: [],
  varianceLoading: false,
  varianceError: null,
  varianceMessage: '',
};

// ============================================================================
// STREAMING THUNK: server fetches + consolidates, UI paints section by section
// ============================================================================
const streamControllers = {};
const streamKeys = {}; // mode -> accounts key of the request currently running

export const abortConsolidatedStreams = () => {
  Object.values(streamControllers).forEach((c) => c.abort());
  Object.keys(streamKeys).forEach((m) => delete streamKeys[m]);
};

export const streamConsolidated = createAsyncThunk(
  'multiAopActuals/streamConsolidated',
  async ({ mode, accountNames, fresh = false }, { dispatch, rejectWithValue }) => {
    const key = `${fresh ? 'fresh|' : ''}${[].concat(accountNames).join(',')}`;
    // Same request already running (e.g. effect re-fired): don't redo the work.
    if (streamKeys[mode] === key && !streamControllers[mode]?.signal.aborted) {
      return { mode, aborted: true };
    }
    streamControllers[mode]?.abort();
    const controller = new AbortController();
    streamControllers[mode] = controller;
    streamKeys[mode] = key;

    dispatch(streamStarted({ mode }));
    try {
      const token = localStorage.getItem('googleIdToken');
      const names = Array.isArray(accountNames)
        ? accountNames.join(',')
        : accountNames;
      // fresh=1 is a "hard refresh": the server bypasses its cache and reloads from the database.
      const url = `${config.apiURL}/api/v1/aop/consolidated-stream?mode=${mode}${fresh ? '&fresh=1' : ''}&accountNames=${encodeURIComponent(names)}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!response.ok || !response.body) {
        throw new Error(`Request failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const handleLine = (line) => {
        if (!line.trim()) return;
        const msg = JSON.parse(line);
        if (msg.type === 'error') throw new Error(msg.message);
        dispatch(streamChunk({ mode, msg }));
      };

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        lines.forEach(handleLine);
      }
      buffer += decoder.decode();
      handleLine(buffer);
      delete streamKeys[mode];
      return { mode };
    } catch (error) {
      if (error.name === 'AbortError') return { mode, aborted: true };
      delete streamKeys[mode];
      return rejectWithValue({
        mode,
        message: error.message || 'Failed to load consolidated data',
      });
    }
  },
);

// ============================================================================
// THUNK 5: Fetch AOP Variance Data (UPDATED WITH COMMA)
// ============================================================================
export const fetchAopVarianceData = createAsyncThunk(
  'multiAopActuals/fetchAopVarianceData',
  async (accountNames, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('googleIdToken');

      // Convert array to a COMMA-separated string
      const formattedAccountNames = Array.isArray(accountNames)
        ? accountNames.join(',')
        : accountNames;

      const response = await axios.get(`${config.apiURL}/api/v1/aop-variance`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { accountNames: formattedAccountNames },
      });

      return response.data?.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          'Failed fetching AOP Variance Data',
      );
    }
  },
);

// ============================================================================
// SLICE
// ============================================================================
const multiAopActualsSlice = createSlice({
  name: 'multiAopActuals',
  initialState,
  reducers: {
    resetMultiAopActualsState: () => {
      abortConsolidatedStreams();
      return initialState;
    },
    streamStarted: (state, { payload: { mode } }) => {
      state[`${mode}Stream`] = {
        ...emptyStream(),
        status: 'loading',
        message: 'Fetching and consolidating data...',
      };
    },
    streamChunk: (state, { payload: { mode, msg } }) => {
      const stream = state[`${mode}Stream`];
      switch (msg.type) {
        case 'section':
          stream.rows.push(...msg.rows);
          break;
        case 'sectionDone':
          stream.sectionsLoaded.push(msg.section);
          (msg.months || []).forEach((m) => {
            if (!stream.months.includes(m)) stream.months.push(m);
          });
          break;
        case 'done':
          stream.status = 'done';
          stream.stale = !!msg.stale;
          break;
        default:
          break;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(streamConsolidated.fulfilled, (state, { payload }) => {
        if (payload.aborted) return;
        const stream = state[`${payload.mode}Stream`];
        stream.status = 'done';
      })
      .addCase(streamConsolidated.rejected, (state, { payload }) => {
        if (!payload) return;
        const stream = state[`${payload.mode}Stream`];
        stream.status = 'error';
        stream.error = payload.message;
      })

      // Variance Data Cases (NEW)
      .addCase(fetchAopVarianceData.pending, (state) => {
        state.varianceLoading = true;
        state.varianceError = null;
        state.varianceMessage = 'Retrieving variance data...';
      })
      .addCase(fetchAopVarianceData.fulfilled, (state, action) => {
        state.varianceLoading = false;
        state.varianceData = action.payload;
        state.varianceMessage = 'Variance data fetched successfully.';
      })
      .addCase(fetchAopVarianceData.rejected, (state, action) => {
        state.varianceLoading = false;
        state.varianceError = action.payload;
        state.varianceMessage = action.payload;
      });
  },
});

export const { resetMultiAopActualsState, streamStarted, streamChunk } =
  multiAopActualsSlice.actions;
export default multiAopActualsSlice.reducer;
